// src/StudentDashboard.js
import React, { useState, useEffect } from "react";
import { auth } from "./firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import styles from "./StudentDashboard.module.css";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import logo from "./optolog_logo.png";
import {
  PieChart, Pie, Cell, Legend, ResponsiveContainer
} from "recharts";
import LogsTab from './LogsTab';
import { collection, query, where, getDocs } from "firebase/firestore";
import { fetchLogStats } from "./utils";
import MySkillsTab from "./MySkillsTab";
import ClinicEventSignup from "./ClinicEventSignup";
import MyClinicEvents from "./MyClinicEvents";




function StudentDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [userName, setUserName] = useState("");
  const [cohort, setCohort] = useState("");
  const [showWelcome, setShowWelcome] = useState(true);
  const [progressValue, setProgressValue] = useState(70);   // Example static progress
  const [goalValue, setGoalValue] = useState(250);          // Example static goal
  const [totalEncounters, setTotalEncounters] = useState(0);
  const [totalExposures, setTotalExposures] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [pedsHours, setPedsHours] = useState(0);
  const pedsGoal = 15; // 🎯 15 hours is your target
  const [ageGroupCounts, setAgeGroupCounts] = useState({
  "0-18": 0,
  "19-69": 0,
  "70+": 0,
});
  const handleLogout = async () => {
  await signOut(auth);
  navigate("/login");
};

const refreshDashboardStats = async () => {
  const userEmail = auth.currentUser?.email;
  if (!userEmail) return;

  await fetchLogStats(
    userEmail,
    setTotalEncounters,
    setTotalExposures,
    setTotalHours,
    setAgeGroupCounts,
    setPedsHours
  );
};

const fetchLogStats = async (
  userEmail,
  setTotalEncounters,
  setTotalExposures,
  setTotalHours,
  setAgeGroupCounts,
  setPedsHours
) => {
  try {
    const logsRef = collection(db, "users", userEmail, "logs");
    const snapshot = await getDocs(logsRef);

    const encounterCount = snapshot.docs.filter(doc => {
      const log = doc.data();
      return log.involvement === "Patient Encounter - Exercised independent judgment";
    }).length;

    let exposureCount = 0;
    let totalMinutes = 0;
    let pedsMinutes = 0;
    const ageCounts = { "0-18": 0, "19-69": 0, "70+": 0 };

    snapshot.forEach((doc) => {
      const log = doc.data();

      if (log.involvement === "Patient Exposure - Screening, Testing, Shadowing") {
        exposureCount += 1;
      }
      if (log.duration) {
        const minutes = Number(log.duration);
        totalMinutes += minutes;
        if (log.serviceType === "Pediatrics") {
          pedsMinutes += minutes;
        }
      }
      if (log.ageGroup && ageCounts[log.ageGroup] !== undefined) {
        ageCounts[log.ageGroup] += 1;
      }
    });

    setTotalEncounters(encounterCount);
    setTotalExposures(exposureCount);
    setTotalHours((totalMinutes / 60).toFixed(1));
    setAgeGroupCounts({ ...ageCounts });
    setPedsHours(Number((pedsMinutes / 60).toFixed(2)));
  } catch (error) {
    console.error("🔥 Firestore log fetch failed:", error.message, error);
  }
};

  useEffect(() => {
  const fetchUserData = async () => {
    if (auth.currentUser) {
      const userRef = doc(db, "users", auth.currentUser.email);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        console.log("✅ Firestore user data:", data);

        setUserName(data.name || "Student");
        setCohort(data.cohort || "");
      }

      await fetchLogStats(
  auth.currentUser.email,
  setTotalEncounters,
  setTotalExposures,
  setTotalHours,
  setAgeGroupCounts,
  setPedsHours  // 👈 Add this!
);
    }
  };

  fetchUserData();

  const timer = setTimeout(() => setShowWelcome(false), 5000);
  return () => clearTimeout(timer);
}, []);


  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <img src={logo} alt="OptoLog Logo" className={styles.logo} />

        <div className={styles.schoolName}>
          Rocky Mountain University - College of Optometric Medicine
        </div>

        <div className={styles.rightSection}>
          <span className={styles.userInfo}>
            {userName} {cohort && `| ${cohort}`}
          </span>
          <button onClick={handleLogout} className={styles.logoutButton}>
            Log Out
          </button>
        </div>
      </div>

      {showWelcome && (
        <div className={styles.welcomeBox}>
          👋 Welcome, {userName}!
        </div>
      )}

      <div className={styles.tabBar}>
  {[
    { key: "home", label: "Home" },
    { key: "logs", label: "Log Encounter/Exposure" },
    { key: "mySkills", label: "My Skills" },
    { key: "clinicSignup", label: "Clinic Event Sign-Up" },
    { key: "myEvents", label: "My Events" }, // ✅ Add this line
  ].map(({ key, label }) => (
    <button
      key={key}
      className={activeTab === key ? styles.activeTab : styles.tab}
      onClick={() => setActiveTab(key)}
    >
      {label}
    </button>
  ))}
</div>



      <div className={styles.content}>
        {activeTab === "home" && (
          <>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryBox}>
                <h3>Total Encounters</h3>
                <p>{totalEncounters}</p>
              </div>
              <div className={styles.summaryBox}>
  <h3>Total Exposures</h3>
  <p>{totalExposures}</p>
</div>
<div className={styles.summaryBox}>
  <h3>Total Hours</h3>
  <p>{totalHours}</p>
</div>
            </div>

            {/* ✅ Analytics Row: Progress Rings + Pie Chart */}
            <div className={styles.analyticsRow}>

              {/* Total Encounters Progress */}
<div className={styles.progressContainer}>
  <h3>Total Encounters</h3>
  <div style={{ width: 200, height: 200, position: 'relative' }}>
    <CircularProgressbar
      value={totalEncounters}
      maxValue={goalValue}
      text={`${totalEncounters} / ${goalValue}`}
      styles={buildStyles({
        textSize: '16px',
        pathColor: '#115680',
        textColor: '#115680',
        trailColor: '#d6d6d6',
      })}
    />
    <div style={{
      position: 'absolute',
      top: '75%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '14px',
      color: '#333',
    }}>
      {Math.round((totalEncounters / goalValue) * 100)}% Complete
    </div>
  </div>
</div>

              {/* Pediatric Hours Progress */}
              <div className={styles.progressContainer}>
                <h3>Pediatric Hours</h3>
                <div style={{ width: 200, height: 200, position: 'relative' }}>
                  <CircularProgressbar
                    value={pedsHours}
                    maxValue={pedsGoal}
                    text={`${pedsHours} / ${pedsGoal}`}
                    styles={buildStyles({
                      textSize: '16px',
                      pathColor: '#FF8C00',
                      textColor: '#115680',
                      trailColor: '#d6d6d6',
                    })}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '75%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '14px',
                    color: '#333',
                  }}>
                    {Math.round((pedsHours / pedsGoal) * 100)}% Complete
                  </div>
                </div>
              </div>

              {/* Age Group Chart */}
              <div className={styles.chartContainer}>
                <h3>Age Group Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
  data={[
    { name: "0-18", value: ageGroupCounts["0-18"] || 0 },
    { name: "19-69", value: ageGroupCounts["19-69"] || 0 },
    { name: "70+", value: ageGroupCounts["70+"] || 0 },
  ]}
  dataKey="value"
  nameKey="name"
  cx="50%"
  cy="50%"
  outerRadius={90}
  label={({ value }) => `${value}`}
  labelLine={false}
>
                      <Cell fill="#00BFFF" />
                      <Cell fill="#FF8C00" />
                      <Cell fill="#DC143C" />
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      wrapperStyle={{ color: "#115680", fontWeight: "600" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

            </div> {/* closes analyticsRow */}
          </>
        )}

        {/* Other tabs */}
{activeTab === "logs" && (
  <LogsTab refreshStats={refreshDashboardStats} />
)}
{activeTab === "mySkills" && <MySkillsTab />}
{activeTab === "clinicSignup" && <ClinicEventSignup />}
{activeTab === "myEvents" && <MyClinicEvents />}


      </div>

    </div>
  );
}

export default StudentDashboard;
